import React, { useEffect, useMemo, useState } from "react"

const CONSENT_KEY = "evoly_cookie_consent_v1"

export type CookieConsentChoice = "accepted" | "rejected"

function getStoredChoice(): CookieConsentChoice | null {
  const raw = window.localStorage.getItem(CONSENT_KEY)
  if (raw === "accepted" || raw === "rejected") {
    return raw
  }
  return null
}

export function hasAcceptedAnalyticsCookies(): boolean {
  if (typeof window === "undefined") return false
  return getStoredChoice() === "accepted"
}

interface CookieConsentBannerProps {
  cookiePolicyPath?: string
  onChoice?: (choice: CookieConsentChoice) => void
}

export const CookieConsentBanner: React.FC<CookieConsentBannerProps> = ({
  cookiePolicyPath = "/cookie-policy",
  onChoice,
}) => {
  const [choice, setChoice] = useState<CookieConsentChoice | null>(null)
  const isVisible = useMemo(() => choice === null, [choice])

  useEffect(() => {
    setChoice(getStoredChoice())
  }, [])

  function saveChoice(next: CookieConsentChoice): void {
    window.localStorage.setItem(CONSENT_KEY, next)
    setChoice(next)
    onChoice?.(next)
  }

  if (!isVisible) return null

  return (
    <div
      className="position-fixed bottom-0 start-0 end-0 p-3"
      style={{ zIndex: 2000 }}
      role="region"
      aria-label="Cookie consent banner"
    >
      <div className="container">
        <div className="card shadow-sm border-0 position-relative">
          <button
            type="button"
            className="btn-close position-absolute top-0 end-0 m-2"
            aria-label="Dismiss cookie banner"
            onClick={() => saveChoice("rejected")}
          />
          <div className="card-body d-flex flex-column flex-lg-row align-items-lg-center gap-3 justify-content-between pe-5">
            <div className="small">
              We use cookies to improve your experience and analyse site traffic.
              You can accept all cookies or reject non-essential cookies.{" "}
              <a href={cookiePolicyPath} target="_blank" rel="noreferrer">
                Cookie Policy
              </a>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => saveChoice("rejected")}
                type="button"
              >
                Reject Non-Essential
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => saveChoice("accepted")}
                type="button"
              >
                Accept All
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

